import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { GalerieSelecteurService } from './galerie-selecteur';
import { environment } from 'src/environments/environment';
import { CandidatAnimal, ReconnaissanceAnimalService, ResultatReconnaissance, StatutReconnaissance } from 'src/app/core/services/entities/reconnaissance-animal.service';

/**
 * « Reconnaître par photo » : la photo d'un animal est comparée aux photos de référence des fiches ;
 * l'utilisateur confirme l'animal (la photo rejoint sa fiche) ou indique qu'aucun ne correspond.
 */
@Component({
  selector: 'bay-reconnaissance-animal',
  template: `
    <button type="button" class="btn fw-bold d-flex align-items-center gap-2 shadow-sm" data-test="reconnaitre"
            style="background:#e5eadf;color:#0d631b;border-radius:14px;padding:8px 16px;" (click)="choisir(fichier)">
      <span class="material-symbols-outlined" style="font-size:18px;">photo_camera</span>
      Reconnaître par photo
    </button>
    <button type="button" class="btn btn-sm d-flex align-items-center gap-1" style="color:#0d631b;" (click)="depuisGalerie()" data-test="reconnaitre-galerie">
      <span class="material-symbols-outlined" style="font-size:16px;" aria-hidden="true">photo_library</span>Galerie Wanar
    </button>
    <input #fichier type="file" accept="image/*" capture="environment" class="d-none" (change)="envoyer($event)">

    <div *ngIf="ouvert" class="bay-reco-fond" (click)="fermer()">
      <div class="bay-reco" role="dialog" aria-modal="true" aria-labelledby="reco-titre" (click)="$event.stopPropagation()">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h2 id="reco-titre" class="fw-bold mb-0" style="font-size:18px;">Reconnaître un animal</h2>
          <button type="button" class="btn-close" aria-label="Fermer" (click)="fermer()"></button>
        </div>

        <div *ngIf="statut && statut.avecPhoto === 0" class="alert alert-warning py-2" style="font-size:13px;">
          Aucun animal n'a encore de photo de référence : ajoutez des photos sur les fiches des animaux pour qu'ils puissent être reconnus.
        </div>

        <div *ngIf="envoi" class="text-center py-4" role="status">
          <span class="spinner-border" style="color:#0d631b;" aria-hidden="true"></span>
          <p class="mt-2 mb-0 text-muted" style="font-size:13px;">Comparaison avec les animaux de la ferme…</p>
        </div>
        <div *ngIf="erreur" class="alert alert-danger py-2" role="alert">{{ erreur }}</div>

        <ng-container *ngIf="!envoi && resultat">
          <div class="d-flex gap-3 mb-3">
            <img *ngIf="apercu" [src]="apercu" alt="Photo envoyée" style="width:96px;height:96px;object-fit:cover;border-radius:12px;">
            <div style="font-size:13px;">
              <p *ngIf="resultat.description" class="mb-1">{{ resultat.description }}</p>
              <p *ngIf="!resultat.photoExploitable" class="mb-1 text-warning fw-semibold">{{ resultat.conseilPhoto || 'Photo difficile à exploiter : reprenez-la de plus près, de profil.' }}</p>
              <p class="mb-0 text-muted">{{ resultat.compares }} animal(aux) comparé(s)<span *ngIf="resultat.restants != null"> · {{ resultat.restants }} reconnaissance(s) restante(s) aujourd'hui</span></p>
            </div>
          </div>

          <p *ngIf="!resultat.candidats?.length" class="text-muted">Aucun animal de la ferme ne ressemble à cette photo.</p>
          <div *ngFor="let c of resultat.candidats" class="d-flex gap-3 align-items-start p-2 mb-2 rounded-3" style="background:#f8faf5;" [attr.data-candidat]="c.animalId">
            <img *ngIf="c.photoId" [bayAuthSrc]="miniature(c.photoId)" alt="" style="width:64px;height:64px;object-fit:cover;border-radius:10px;background:#e5e7eb;">
            <div class="flex-grow-1" style="font-size:13px;">
              <div class="fw-bold" style="font-size:15px;">{{ nomCandidat(c) }}
                <span class="badge rounded-pill ms-1" [style.background]="c.confiance === 'HAUTE' ? '#dcfce7' : c.confiance === 'MOYENNE' ? '#fef3c7' : '#f3f4f6'"
                      [style.color]="c.confiance === 'HAUTE' ? '#0d631b' : c.confiance === 'MOYENNE' ? '#92400e' : '#374151'">
                  {{ c.confiance === 'HAUTE' ? 'Très ressemblant' : c.confiance === 'MOYENNE' ? 'Ressemblant' : 'Peu sûr' }}</span>
              </div>
              <div class="text-muted">{{ detailCandidat(c) }}</div>
              <div *ngIf="c.ressemblances">✓ {{ c.ressemblances }}</div>
              <div *ngIf="c.differences" class="text-muted">≠ {{ c.differences }}</div>
            </div>
            <button type="button" class="btn btn-sm btn-success flex-shrink-0" [disabled]="retourEnCours" (click)="confirmer(c)">C'est lui</button>
          </div>

          <div class="d-flex flex-wrap gap-2 mt-2">
            <button *ngIf="resultat.proposerApprofondi" type="button" class="btn btn-sm btn-outline-success" (click)="approfondir()">Analyse approfondie</button>
            <button *ngIf="resultat.candidats?.length" type="button" class="btn btn-sm btn-light" [disabled]="retourEnCours" (click)="aucun()">Aucun de ceux-ci</button>
            <button type="button" class="btn btn-sm btn-light" (click)="choisir(fichier)">Autre photo</button>
          </div>
        </ng-container>
      </div>
    </div>
  `,
  styles: [`
    .bay-reco-fond { position:fixed; inset:0; background:rgba(17,24,39,.45); z-index:1060; display:flex; align-items:center; justify-content:center; padding:16px; }
    .bay-reco { background:#fff; border-radius:20px; padding:20px; width:100%; max-width:560px; max-height:90vh; overflow:auto; }
  `]
})
export class ReconnaissanceAnimalComponent {
  /** Limite la comparaison aux animaux d'un troupeau (facultatif) */
  @Input() troupeauId?: number;

  ouvert = false;
  envoi = false;
  retourEnCours = false;
  erreur: string | null = null;
  resultat?: ResultatReconnaissance;
  statut?: StatutReconnaissance;
  apercu?: string;

  constructor(private service: ReconnaissanceAnimalService, private router: Router, private selecteur: GalerieSelecteurService) {}

  nomCandidat(c: CandidatAnimal): string {
    return [c.numero, c.nom].filter(x => !!x).join(' · ') || 'Animal ' + c.animalId;
  }

  detailCandidat(c: CandidatAnimal): string {
    const genre = c.genre === 'MALE' ? 'mâle' : c.genre === 'FEMELLE' ? 'femelle' : null;
    return [c.espece, c.race, genre].filter(x => !!x).join(' · ');
  }

  miniature(id: number): string { return `${environment.BASE_URL}/api/documents/${id}/miniature`; }

  choisir(input: HTMLInputElement): void {
    input.value = '';
    input.click();
  }

  /** Photo de la Galerie Wanar : envoyée comme un nouveau fichier (image d'affichage) */
  async depuisGalerie(): Promise<void> {
    const f = await this.selecteur.choisirFichier().catch(() => null);
    if (f) { this.envoyerFichier(f); }
  }

  envoyer(ev: Event): void {
    const f = (ev.target as HTMLInputElement).files?.[0];
    if (f) { this.envoyerFichier(f); }
  }

  private envoyerFichier(f: File): void {
    this.ouvert = true;
    this.envoi = true;
    this.erreur = null;
    this.resultat = undefined;
    if (this.apercu) { URL.revokeObjectURL(this.apercu); }
    this.apercu = URL.createObjectURL(f);
    this.service.statut(this.troupeauId).subscribe({ next: s => this.statut = s, error: () => {} });
    this.service.identifier(f, this.troupeauId).subscribe({
      next: r => { this.resultat = r; this.envoi = false; },
      error: e => { this.envoi = false; this.erreur = e?.error?.message || 'La reconnaissance n\'a pas pu être faite. Réessayez.'; }
    });
  }

  approfondir(): void {
    if (!this.resultat) { return; }
    this.envoi = true;
    this.service.approfondir(this.resultat.id).subscribe({
      next: r => { this.resultat = r; this.envoi = false; },
      error: e => { this.envoi = false; this.erreur = e?.error?.message || 'L\'analyse approfondie n\'a pas pu être faite.'; }
    });
  }

  /** L'animal est confirmé : la photo rejoint sa fiche, qui s'ouvre */
  confirmer(c: CandidatAnimal): void {
    if (!this.resultat) { return; }
    this.retourEnCours = true;
    this.service.retour(this.resultat.id, c.animalId, true).subscribe({
      next: () => { this.retourEnCours = false; this.fermer(); this.router.navigate(['/betail/details', c.animalId]); },
      error: e => { this.retourEnCours = false; this.erreur = e?.error?.message || 'La confirmation n\'a pas été enregistrée.'; }
    });
  }

  aucun(): void {
    if (!this.resultat) { return; }
    this.retourEnCours = true;
    this.service.retour(this.resultat.id, null, false).subscribe({
      next: () => { this.retourEnCours = false; this.fermer(); },
      error: () => { this.retourEnCours = false; this.fermer(); }
    });
  }

  fermer(): void {
    this.ouvert = false;
    if (this.apercu) { URL.revokeObjectURL(this.apercu); this.apercu = undefined; }
  }
}
